import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { icons } from '../../components/ui/Icons';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Card, CardContent, CardHeader } from '../../components/ui/Card';
import { Table } from '../../components/ui/Table';
import { Select } from '../../components/ui/Select';
import { Alert } from '../../components/ui/Alert';
import { EmptyState } from '../../components/ui/EmptyState';
import { ErrorState } from '../../components/ui/ErrorState';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { Breadcrumb } from '../../components/ui/Breadcrumb';
import { Dialog } from '../../components/ui/Dialog';
import { DiscordRole, RolePermission, Permission } from '../../types/auth';
import { requestJson, getErrorMessage, isAbortError } from '../../lib/adminApi';

type RoleStatusFilter = 'all' | 'active' | 'inactive';

const USER_STATUS_OPTIONS = [
  { value: 'all', label: 'Alle Status' },
  { value: 'active', label: 'Aktiv' },
  { value: 'inactive', label: 'Inaktiv' },
];

const PERMISSION_GROUPS = [
  'Dashboard',
  'Patienten',
  'Medizin',
  'Personal',
  'Organisation',
  'Dokumente',
  'Kommunikation',
  'Verwaltung',
  'System',
] as const;

type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

const PERMISSION_CATEGORY_MAP: Record<string, PermissionGroup> = {
  Dashboard: 'Dashboard',
  'Patienten & Stationen': 'Patienten',
  Pflege: 'Patienten',
  Medizin: 'Medizin',
  Personal: 'Personal',
  Organisation: 'Organisation',
  Wissen: 'Dokumente',
  Kommunikation: 'Kommunikation',
  'Sicherheit & Notfall': 'Organisation',
  Betrieb: 'Organisation',
  Verwaltung: 'Verwaltung',
  System: 'System',
};

const GROUP_ICONS: Record<PermissionGroup, keyof typeof icons> = {
  Dashboard: 'LayoutDashboard',
  Patienten: 'Bed',
  Medizin: 'Stethoscope',
  Personal: 'Users',
  Organisation: 'Calendar',
  Dokumente: 'FileText',
  Kommunikation: 'MessageSquare',
  Verwaltung: 'Settings',
  System: 'Cpu',
};

function getPermissionGroup(permission: Permission): PermissionGroup {
  return PERMISSION_CATEGORY_MAP[permission.category] || 'System';
}

function groupPermissions(permissions: Permission[]): Record<PermissionGroup, Permission[]> {
  const groups = Object.fromEntries(
    PERMISSION_GROUPS.map((group) => [group, [] as Permission[]]),
  ) as Record<PermissionGroup, Permission[]>;

  permissions.forEach((permission) => {
    groups[getPermissionGroup(permission)].push(permission);
  });

  Object.values(groups).forEach((group) => {
    group.sort((a, b) => a.name.localeCompare(b.name, 'de-DE'));
  });

  return groups;
}

function normalizeRole(value: unknown): DiscordRole {
  const data = value as Partial<DiscordRole>;
  return {
    id: data.id || '',
    discordRoleId: data.discordRoleId || '',
    name: data.name || 'Unbenannte Rolle',
    color: typeof data.color === 'string' ? data.color : null,
    isActive: data.isActive ?? false,
    permissionCount: Number(data.permissionCount) || 0,
    userCount: Number(data.userCount) || 0,
  };
}

function normalizeRolePermission(value: unknown): RolePermission {
  const data = value as Partial<RolePermission>;
  return {
    permissionId: data.permissionId || '',
    permissionKey: data.permissionKey || '',
    permissionName: data.permissionName || data.permissionKey || 'Unbekannte Berechtigung',
    category: data.category || 'System',
  };
}

export function RolesListPage() {
  const [roles, setRoles] = useState<DiscordRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<RoleStatusFilter>('all');

  const [selectedRole, setSelectedRole] = useState<DiscordRole | null>(null);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  const [rolePermissionsLoading, setRolePermissionsLoading] = useState(false);
  const [rolePermissionsError, setRolePermissionsError] = useState<string | null>(null);
  const [rolePermissionIds, setRolePermissionIds] = useState<string[]>([]);
  const [roleOriginalPermissionIds, setRoleOriginalPermissionIds] = useState<string[]>([]);
  const [roleSaving, setRoleSaving] = useState(false);
  const [roleMutationMessage, setRoleMutationMessage] = useState<{ variant: 'success' | 'error'; text: string } | null>(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ variant: 'success' | 'error'; text: string } | null>(null);

  const [availablePermissions, setAvailablePermissions] = useState<Permission[]>([]);

  const roleRequestRef = useRef<AbortController | null>(null);

  const fetchRoles = useCallback(async () => {
    setRolesLoading(true);
    setRolesError(null);
    try {
      const data = await requestJson<unknown[]>(`/discord-roles`);
      setRoles(Array.isArray(data) ? data.map(normalizeRole) : []);
    } catch (error) {
      setRolesError(getErrorMessage(error, 'Discord-Rollen konnten nicht geladen werden.'));
    } finally {
      setRolesLoading(false);
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const data = await requestJson<unknown[]>(`/permissions`);
      setAvailablePermissions(Array.isArray(data) ? data.map((item) => {
        const permission = item as Partial<Permission>;
        return {
          id: permission.id || '',
          key: permission.key || '',
          name: permission.name || permission.key || 'Unbenannte Berechtigung',
          category: permission.category || 'System',
          description: typeof permission.description === 'string' ? permission.description : null,
        };
      }) : []);
    } catch (error) {
      console.error('Failed to load permissions:', error);
    }
  }, []);

  const syncDiscordRoles = useCallback(async () => {
    setSyncLoading(true);
    setSyncMessage(null);
    try {
      const result = await requestJson<{ created?: number; updated?: number; deactivated?: number }>(
        `/discord-roles/sync`,
        { method: 'POST' },
      );
      await fetchRoles();
      const created = result.created || 0;
      const updated = result.updated || 0;
      const deactivated = result.deactivated || 0;
      setSyncMessage({
        variant: 'success',
        text: `Synchronisation abgeschlossen: ${created} erstellt, ${updated} aktualisiert, ${deactivated} deaktiviert.`,
      });
    } catch (error) {
      setSyncMessage({
        variant: 'error',
        text: getErrorMessage(error, 'Discord-Rollen konnten nicht synchronisiert werden.'),
      });
    } finally {
      setSyncLoading(false);
    }
  }, [fetchRoles]);

  const loadRoleDetail = useCallback(async (discordRoleId: string) => {
    roleRequestRef.current?.abort();
    const controller = new AbortController();
    roleRequestRef.current = controller;
    setRolePermissionsLoading(true);
    setRolePermissionsError(null);

    try {
      const data = await requestJson<unknown[]>(`/discord-roles/${encodeURIComponent(discordRoleId)}/permissions`, {
        signal: controller.signal,
      });
      const permissions = Array.isArray(data) ? data.map(normalizeRolePermission) : [];
      setRolePermissions(permissions);
      const ids = permissions.map((permission) => permission.permissionId).filter(Boolean);
      setRolePermissionIds(ids);
      setRoleOriginalPermissionIds(ids);
    } catch (error) {
      if (!isAbortError(error)) {
        setRolePermissionsError(getErrorMessage(error, 'Rollenberechtigungen konnten nicht geladen werden.'));
      }
    } finally {
      if (roleRequestRef.current === controller) {
        roleRequestRef.current = null;
      }
      setRolePermissionsLoading(false);
    }
  }, []);

  const handleRoleSelect = useCallback((role: DiscordRole) => {
    setSelectedRole(role);
    setRoleMutationMessage(null);
    void loadRoleDetail(role.discordRoleId);
  }, [loadRoleDetail]);

  const closeDialog = useCallback(() => {
    setSelectedRole(null);
    setRolePermissions([]);
    setRolePermissionIds([]);
    setRoleOriginalPermissionIds([]);
    setRolePermissionsError(null);
    setRoleMutationMessage(null);
  }, []);

  const saveRolePermissions = useCallback(async () => {
    if (!selectedRole) {
      return;
    }

    setRoleSaving(true);
    setRoleMutationMessage(null);
    try {
      await requestJson<{ success: boolean }>(
        `/discord-roles/${encodeURIComponent(selectedRole.discordRoleId)}/permissions`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ permissionIds: rolePermissionIds }),
        },
      );
      await Promise.all([fetchRoles(), loadRoleDetail(selectedRole.discordRoleId)]);
      setRoleOriginalPermissionIds(rolePermissionIds);
      setRoleMutationMessage({ variant: 'success', text: 'Rollenberechtigungen wurden gespeichert.' });
    } catch (error) {
      setRoleMutationMessage({
        variant: 'error',
        text: getErrorMessage(error, 'Rollenberechtigungen konnten nicht gespeichert werden.'),
      });
    } finally {
      setRoleSaving(false);
    }
  }, [selectedRole, rolePermissionIds, fetchRoles, loadRoleDetail]);

  const filteredRoles = useMemo(() => {
    const query = search.trim().toLowerCase();
    return roles.filter((role) => {
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && role.isActive) ||
        (statusFilter === 'inactive' && !role.isActive);
      return matchesStatus && (!query || role.name.toLowerCase().includes(query));
    });
  }, [roles, search, statusFilter]);

  const rolePermissionCatalog = useMemo(() => {
    const catalog = new Map<string, Permission>(
      availablePermissions.map((permission) => [permission.id, permission]),
    );
    rolePermissions.forEach((permission) => {
      if (!catalog.has(permission.permissionId)) {
        catalog.set(permission.permissionId, {
          id: permission.permissionId,
          key: permission.permissionKey,
          name: permission.permissionName,
          category: permission.category,
          description: null,
        });
      }
    });
    return Array.from(catalog.values());
  }, [availablePermissions, rolePermissions]);

  const groupedRolePermissions = useMemo(
    () => groupPermissions(rolePermissionCatalog),
    [rolePermissionCatalog],
  );

  const roleHasChanges = useMemo(() => {
    const current = new Set(rolePermissionIds);
    const original = new Set(roleOriginalPermissionIds);
    if (current.size !== original.size) {
      return true;
    }
    return Array.from(current).some((id) => !original.has(id));
  }, [rolePermissionIds, roleOriginalPermissionIds]);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
    return () => roleRequestRef.current?.abort();
  }, [fetchRoles, fetchPermissions]);

  const breadcrumbItems = [
    { label: 'Administration', href: '/admin' },
    { label: 'Benutzer & Zugriff', href: '/admin/access' },
    { label: 'Discord-Rollen', current: true },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Discord-Rollen</h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Rollen aus Discord anzeigen, synchronisieren und gruppierte Berechtigungen verwalten.
          </p>
        </div>
        <Button variant="outline" size="sm" leftIcon="RefreshCw" loading={syncLoading} onClick={() => void syncDiscordRoles()}>
          Discord synchronisieren
        </Button>
      </div>

      <Card className="border-neutral-200 dark:border-neutral-700">
        <CardContent className="space-y-4">
          {syncMessage && (
            <Alert
              variant={syncMessage.variant === 'success' ? 'success' : 'error'}
              dismissible
              onDismiss={() => setSyncMessage(null)}
            >
              {syncMessage.text}
            </Alert>
          )}
          {rolesError && roles.length === 0 ? (
            <ErrorState description={rolesError} onRetry={() => void fetchRoles()} />
          ) : rolesError ? (
            <Alert variant="error" title="Rollen konnten nicht vollständig geladen werden" dismissible onDismiss={() => setRolesError(null)}>
              {rolesError}
            </Alert>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_12rem]">
            <Input
              aria-label="Discord-Rollen durchsuchen"
              placeholder="Rolle suchen ..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              leftIcon="Search"
            />
            <Select
              aria-label="Rollenstatus filtern"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as RoleStatusFilter)}
              options={USER_STATUS_OPTIONS}
            />
          </div>

          {rolesLoading && roles.length === 0 ? (
            <SkeletonTable rows={5} columns={4} />
          ) : filteredRoles.length === 0 ? (
            <EmptyState
              icon="Shield"
              title="Keine Discord-Rollen gefunden"
              description="Es sind keine Rollen vorhanden oder die Suche liefert aktuell keine Treffer."
            />
          ) : (
            <Table
              columns={[
                {
                  key: 'name',
                  header: 'Rolle',
                  className: 'min-w-[240px]',
                  render: (role) => (
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        className="h-4 w-4 flex-shrink-0 rounded-full border border-neutral-300 dark:border-neutral-600"
                        style={{ backgroundColor: role.color || undefined }}
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <p className="truncate font-medium text-neutral-900 dark:text-white">{role.name}</p>
                        <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">{role.discordRoleId || '-'}</p>
                      </div>
                    </div>
                  ),
                },
                {
                  key: 'permissionCount',
                  header: 'Berechtigungen',
                  className: 'w-32',
                  render: (role) => <span className="font-mono text-sm">{role.permissionCount}</span>,
                },
                {
                  key: 'userCount',
                  header: 'Mitglieder',
                  className: 'w-28',
                  render: (role) => <span className="text-sm text-neutral-700 dark:text-neutral-300">{role.userCount || '-'}</span>,
                },
                {
                  key: 'isActive',
                  header: 'Status',
                  className: 'w-28',
                  render: (role) => (
                    <Badge variant={role.isActive ? 'medical' : 'gray'}>{role.isActive ? 'Aktiv' : 'Inaktiv'}</Badge>
                  ),
                },
                {
                  key: 'actions',
                  header: 'Aktionen',
                  className: 'w-24',
                  render: (role) => (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleRoleSelect(role);
                      }}
                    >
                      Bearbeiten
                    </Button>
                  ),
                },
              ]}
              data={filteredRoles}
              keyExtractor={(role) => role.id}
              rowClick={handleRoleSelect}
              loading={rolesLoading && roles.length > 0}
              emptyMessage="Keine Rollen entsprechen den aktuellen Filtern."
            />
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!selectedRole}
        onClose={closeDialog}
        title={selectedRole ? `Rollenberechtigungen: ${selectedRole.name}` : 'Rollenberechtigungen'}
        description="Berechtigungen sind nach Fachbereichen gruppiert. Änderungen werden erst nach dem Speichern übernommen."
        size="full"
        closeOnBackdrop={!roleSaving}
        closeOnEscape={!roleSaving}
      >
        {(selectedRole && rolePermissionsLoading) && (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" aria-hidden="true" />
          </div>
        )}
        {selectedRole && rolePermissionsError && (
          <Alert variant="error" title="Rollenberechtigungen nicht verfügbar" dismissible onDismiss={() => setRolePermissionsError(null)}>
            {rolePermissionsError}
          </Alert>
        )}
        {roleMutationMessage && (
          <Alert
            variant={roleMutationMessage.variant === 'success' ? 'success' : 'error'}
            dismissible
            onDismiss={() => setRoleMutationMessage(null)}
          >
            {roleMutationMessage.text}
          </Alert>
        )}

        {selectedRole && !rolePermissionsLoading && (
          <>
            <div className="flex flex-wrap items-center gap-3 rounded-lg bg-neutral-50 dark:bg-neutral-900/40 p-4 mb-4">
              <span
                className="h-4 w-4 rounded-full border border-neutral-300 dark:border-neutral-600"
                style={{ backgroundColor: selectedRole.color || undefined }}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-neutral-900 dark:text-white">{selectedRole.name}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">{selectedRole.discordRoleId}</p>
              </div>
              <Badge variant={selectedRole.isActive ? 'medical' : 'gray'}>{selectedRole.isActive ? 'Aktiv' : 'Inaktiv'}</Badge>
              <Badge variant="primary">{rolePermissionIds.length} ausgewählt</Badge>
            </div>

            {PERMISSION_GROUPS.some((group) => groupedRolePermissions[group].length > 0) ? (
              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                {PERMISSION_GROUPS.map((group) => {
                  const permissions = groupedRolePermissions[group];
                  if (permissions.length === 0) {
                    return null;
                  }
                  const GroupIcon = icons[GROUP_ICONS[group]];
                  return (
                    <Card key={group} className="border-neutral-200 dark:border-neutral-700">
                      <CardHeader className="mb-3 flex items-center gap-3 pb-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400">
                          <GroupIcon className="h-4 w-4" aria-hidden="true" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-semibold text-neutral-900 dark:text-white">{group}</h4>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">{permissions.length} Berechtigungen</p>
                        </div>
                      </CardHeader>
                      <CardContent className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                        {permissions.map((permission) => {
                          const checked = rolePermissionIds.includes(permission.id);
                          return (
                            <label
                              key={permission.id}
                              className={cn(
                                'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                                checked
                                  ? 'border-primary-300 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20'
                                  : 'border-neutral-200 bg-white hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800/40 dark:hover:bg-neutral-800/60',
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(event) => {
                                  setRolePermissionIds((current) =>
                                    event.target.checked
                                      ? [...current, permission.id]
                                      : current.filter((id) => id !== permission.id),
                                  );
                                }}
                                className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                              />
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-medium text-neutral-900 dark:text-white whitespace-normal break-words">{permission.name}</span>
                                <span className="block font-mono text-[11px] text-neutral-500 dark:text-neutral-400">{permission.key}</span>
                              </span>
                            </label>
                          );
                        })}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                icon="Key"
                title="Keine Berechtigungen verfügbar"
                description="Der Berechtigungskatalog konnte keine Einträge für diese Rolle bereitstellen."
              />
            )}

            <div className="flex flex-col-reverse items-stretch justify-between gap-3 border-t border-neutral-200 dark:border-neutral-700 pt-4 sm:flex-row sm:items-center mt-4">
              <span className="text-xs text-neutral-500 dark:text-neutral-400">
                {roleHasChanges ? 'Es liegen ungespeicherte Änderungen vor.' : 'Keine offenen Änderungen.'}
              </span>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => {
                  setRolePermissionIds(roleOriginalPermissionIds);
                  setRoleMutationMessage(null);
                }}>
                  Änderungen verwerfen
                </Button>
                <Button size="sm" leftIcon="Save" loading={roleSaving} disabled={!roleHasChanges} onClick={() => void saveRolePermissions()}>
                  Berechtigungen speichern
                </Button>
              </div>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}